<template>
  <dialog
    class="RunInviteModalView"
    data-theme="light"
    aria-label="Invite a player to the run"
    open
  >
    <div
      class="RunInviteModalView__backdrop"
      data-testid="backdrop"
      @click="$emit('close')"
    />
    <section
      class="RunInviteModalView__window"
    >
      <h2 class="RunInviteModalView__runMeta">
        {{ formattedShortDate }} @ {{ formattedTimeRange }}
      </h2>
      <div class="RunInviteModalView__qrCodeContainer">
        <figure
          class="RunInviteModalView__qrCode"
        >
          <Transition>
            <div
              v-if="qrSvg"
              class="RunInviteModalView__qrCodeSvgContainer"
              v-html="qrSvg"
            />
          </Transition>
          <div class="RunInviteModalView__qrCodeBasketballContainer">
            <Basketball class="RunInviteModalView__qrCodeBasketball" />
          </div>
        </figure>
      </div>
      <h1 class="RunInviteModalView__title">
        Scan to join the run
      </h1>
      <div class="RunInviteModalView__divider">
        or
      </div>
      <FormButton @click="handleShareLink()">
        <span v-if="!isUrlCopied">
          Copy the invite link
        </span>
        <span v-else>
          Copied!
        </span>
      </FormButton>
    </section>
  </dialog>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import FormButton from "@/components/FormButton.vue";
import useRunSchedule from "@/composables/useRunSchedule";
import { supabaseClient } from "@/lib/database/supabase";
import getAppUrl from "@/lib/utils/http/getAppUrl";
import useAuthStore from "@/stores/useAuthStore";
import { RunInvite, RunWithMaybeCourt } from "@shared/lib/database/query.types";
import delay from "@shared/lib/utils/async/delay";
import { storeToRefs } from "pinia";
import QRCode from "qrcode";
import { computed, onBeforeMount, ref, watch } from "vue";

const emit = defineEmits<{
  close: [],
}>();

const {
  run,
} = defineProps<{ run: RunWithMaybeCourt }>();

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const {
  waitForPlayer,
} = useAuthStore();

const runInvite = ref<RunInvite | null>(null);
const qrSvg = ref<string>();
const isUrlCopied = ref(false);

const {
  formattedShortDate,
  formattedTimeRange,
} = useRunSchedule(ref(run));

onBeforeMount(async () => {
  await waitForPlayer();
  if (!currentPlayer.value) return;

  const {
    data: _runInvite,
    error,
  } = await supabaseClient
    .from("run_invites")
    .upsert({
      run_id: run.id,
      player_id: currentPlayer.value.id,
    }, {
      onConflict: "run_id, player_id",
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (_runInvite) {
    runInvite.value = _runInvite;
  } else if (error) {
    emit("close");
    alert(error.message);
  }
});

const joinUrl = computed(() => {
  if (!runInvite.value) return;

  return getAppUrl(`/run-invites/${runInvite.value.id}`);
});

watch(joinUrl, async () => {
  if (!joinUrl.value) return;

  qrSvg.value = await QRCode.toString(joinUrl.value, {
    type: "svg",
    errorCorrectionLevel: "H",
    margin: 2,
  });
});

async function handleShareLink () {
  if (!joinUrl.value) return;

  await navigator.clipboard.writeText(joinUrl.value);
  isUrlCopied.value = true;
  await delay(1000);
  isUrlCopied.value = false;
}
</script>

<style lang="scss" scoped>
.RunInviteModalView {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 24px 16px;

  transition: none 350ms;
}

.RunInviteModalView__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  transition: opacity 335ms cubic-bezier(0.25, 1, 0.30, 1);

  .v-leave-active & {
    transition: 350ms;
    transition-timing-function: initial;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.RunInviteModalView__window {
  display: grid;
  width: 100%;
  padding: 24px 20px;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  .v-enter-active & {
    transition: 335ms cubic-bezier(0.25, 1, 0.30, 1);
    transition-property: opacity, transform;
  }

  .v-leave-active & {
    transition: 350ms;
    transition-property: opacity;
  }

  .v-enter-from & {
    transform: scale(0.8);
    opacity: 0;
  }

  .v-leave-to & {
    opacity: 0;
  }
}

.RunInviteModalView__track {
  display: grid;
  grid-auto-columns: 100%;
  grid-auto-flow: column;
  overflow-x: auto;

  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.RunInviteModalView__card {
  padding: 24px 20px;
  scroll-snap-align: center;
  scroll-snap-stop: always;
}

.RunInviteModalView__runMeta {
  margin-bottom: calc(22px - var(--fontSafariOffset));

  font-size: 16px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;
}

.RunInviteModalView__qrCodeContainer {
  display: flex;
  justify-content: center;
}

.RunInviteModalView__qrCode {
  position: relative;

  flex: 1 1 auto;
  max-width: 400px;
  aspect-ratio: 1;
  overflow: hidden;

  border: solid 1px var(--themeColorNeutral100);
  border-radius: 8px;
}

.RunInviteModalView__qrCodeSvgContainer {
  transition: 350ms opacity;

  &.v-enter-from {
    opacity: 0;
  }

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.RunInviteModalView__qrCodeBasketballContainer {
  --pixelSize: 10;
  --size: calc(var(--pixelSize) / 49 * 100%);
  --borderSize: calc(1 / var(--pixelSize) * 100%);
  --center: calc(50% - var(--size) / 2);
  position: absolute;
  top: var(--center);
  top: round(var(--center), 1px);
  left: var(--center);
  left: round(var(--center), 1px);
  isolation: isolate;

  width: var(--size);
  height: var(--size);

  &::before {
    position: absolute;
    inset: calc(-2 * var(--borderSize));
    z-index: -1;

    border-radius: 50%;
    background-color: var(--colorOffBlack);

    content: "";
  }

  &::after {
    position: absolute;
    inset: calc(-1 * var(--borderSize));
    z-index: -1;

    border-radius: 50%;
    background-color: var(--colorOffWhite);

    content: "";
  }
}

.RunInviteModalView__qrCodeBasketball {
  width: 100%;
  height: 100%;
}

.RunInviteModalView__title {
  margin-top: 24px;

  font-size: 24px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;
}

.RunInviteModalView__divider {
  position: relative;
  isolation: isolate;

  margin: 16px 0 24px;

  color: var(--themeColorNeutral400);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: center;

  &::before {
    position: absolute;
    top: 50%;
    left: 0;
    z-index: -1;

    width: 100%;

    border-top: solid 1px var(--themeColorNeutral100);

    content: "";
  }

  &::after {
    position: absolute;
    left: 50%;
    z-index: -1;
    transform: translateX(-50%);

    width: 36px;
    height: 100%;

    background-color: var(--themeColorNeutral0);

    content: "";
  }
}
</style>
